import { Link } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Hero } from '@/components/sections/Hero';
import { ServicesGrid } from '@/components/sections/ServicesGrid';
import { ProcessSteps } from '@/components/sections/ProcessSteps';
import { WhyUs } from '@/components/sections/WhyUs';
import { CtaBlock } from '@/components/sections/CtaBlock';
import { PullQuote } from '@/components/ui/PullQuote';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { DeskWork } from '@/illustrations/DeskWork';
import { whoWeWorkWith } from '@/data/studio';
import { useSeo } from '@/lib/seo';

const studioProcess = [
  { step: '01', title: 'Listen', description: 'One hour on a call, written notes, a short brief you sign off. No work starts without it.' },
  { step: '02', title: 'Shape', description: 'Three directions or sitemap drafts, written rationale for each, your feedback in plain language.' },
  { step: '03', title: 'Build', description: 'Design or code, shared in Figma and live preview links. You watch it grow, we tune to your notes.' },
  { step: '04', title: 'Ship', description: 'Final files, handover document, and a call to hand it to your team. Then a month of defect fixes.' },
];

export default function Home() {
  useSeo({
    title: 'JUST SEE LTD - Design & Development Studio',
    description: 'A Cyprus-based studio building logos, websites, and identities that refuse to look generic.',
  });

  return (
    <>
      <Hero
        eyebrow="JUST SEE · Cyprus"
        headline={
          <>
            It's never <em className="italic font-light">just</em> a website.
          </>
        }
        description="Logos, websites, and production-grade code for people who care what their work looks like."
        actions={
          <>
            <Button to="/contact" variant="primary" withArrow>
              Start a project
            </Button>
            <Button to="/services" variant="secondary">
              See our services
            </Button>
          </>
        }
        illustration={<DeskWork className="w-full max-w-lg" />}
      />

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="mx-auto max-w-[1440px]">
          <PullQuote>
            We build brands and websites the way a tailor cuts a suit - to the person, not to the average. Templates make everyone look the same. We refuse to do that.
          </PullQuote>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-24 border-t border-border">
        <div className="mx-auto max-w-[1440px] flex flex-col gap-12">
          <SectionHeading
            eyebrow="Who we work with"
            title={<>New brands, small teams, serious work.</>}
            description="We are built for the scale where the person drawing the logo is the person picking up the phone."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {whoWeWorkWith.map((item) => (
              <div key={item.label} className="flex flex-col gap-2 border-t border-border pt-5">
                <h3 className="font-display text-2xl font-medium">{item.label}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicesGrid />

      <ProcessSteps
        title="Four steps, clearly."
        description="No mystery around our method. You always know what comes next, when it is due, and why."
        steps={studioProcess}
      />

      <WhyUs />

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-24 bg-surface">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-end">
          <SectionHeading
            eyebrow="Rough numbers"
            title={<>Prices you can plan around.</>}
            description="Starting points in EUR. Every project gets a fixed quote after a thirty-minute call."
          />
          <dl className="grid sm:grid-cols-3 gap-6">
            <div className="border-t border-border pt-4">
              <dt className="mono-meta text-ink-muted">Logo design</dt>
              <dd className="font-display text-4xl font-semibold mt-2">from €700</dd>
            </div>
            <div className="border-t border-border pt-4">
              <dt className="mono-meta text-ink-muted">Website design</dt>
              <dd className="font-display text-4xl font-semibold mt-2">from €1,200</dd>
            </div>
            <div className="border-t border-border pt-4">
              <dt className="mono-meta text-ink-muted">Website development</dt>
              <dd className="font-display text-4xl font-semibold mt-2">from €1,600</dd>
            </div>
          </dl>
        </div>
        <div className="mx-auto max-w-[1440px] mt-8">
          <Link to="/pricing" className="mono-meta text-accent inline-flex items-center gap-1 hover:underline underline-offset-4">See full pricing →</Link>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
